import { useEffect, useState } from 'react';
import { api } from '../api';
import { useApp } from '../contexts/AppContext';

/**
 * UsageMeter — renders a billing meter at one of three scopes:
 *
 *   scope="me"     → caller's user-scoped usage in the current org
 *   scope="org"    → org-wide usage (admin only)
 *   scope="case"   → spend on a single case (requires `caseId`)
 *
 * Variants:
 *   compact   — inline pill (NavBar / case header)
 *   detailed  — large card (settings page)
 *
 * Props:
 *   period           — 'current' (default) | 'last' | 'all' | 'YYYY-MM'
 *   showPeriodSelector — render an inline dropdown to flip period (default false)
 *   refreshKey       — bump to force a refetch
 */
export default function UsageMeter({
  scope = 'me',
  caseId,
  variant = 'compact',
  period: periodProp = 'current',
  showPeriodSelector = false,
  refreshKey = 0,
}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState(periodProp);
  const { pricing } = useApp();

  useEffect(() => { setPeriod(periodProp); }, [periodProp]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let path;
        if (scope === 'case') {
          if (!caseId) throw new Error('UsageMeter scope="case" requires caseId');
          path = `/usage/case/${encodeURIComponent(caseId)}`;
        } else if (scope === 'org') {
          path = '/usage/org';
        } else {
          path = '/usage/me';
        }
        const params = period && period !== 'current' ? { period } : null;
        const result = await api.get(path, params);
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) setError(err.message || 'failed to load usage');
      }
    })();
    return () => { cancelled = true; };
  }, [scope, caseId, period, refreshKey]);

  if (error) return <div className="usage-meter-error" title={error}>Usage unavailable</div>;
  if (!data) return <div className="usage-meter-loading">…</div>;

  const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
  const total = data.total_client_usd || 0;
  const allowance = data.allowance_client_usd;
  const mode = data.meter_display_mode || 'cost';
  const overflow = data.overflow_active;
  const periodLabel = data.billing_period || period;
  // The pill's label says WHICH total this is — "Usage" alone read as the
  // same thing as the case header's "Case spend". This one is the month's
  // total across every case (the org's for an admin, your own otherwise).
  const usageLabel = { current: 'This month', last: 'Last month', all: 'All time' }[period] || 'Usage';
  const usageScope = scope === 'org' ? "Your organization's usage" : 'Your usage';
  // The org's own spending ceiling is org-wide, so it only reads against the
  // org total, and only for the month it applies to.
  const ceiling = scope === 'org' && period === 'current' ? data.ceiling_client_usd : null;
  // Master cost-visibility switch (org PricingDisplayConfig.show_costs).
  // When off, never render a dollar figure anywhere in the meter.
  const hideCosts = pricing?.show_costs === false;

  const periodPicker = showPeriodSelector ? (
    <select
      className="meter-period-select"
      value={period}
      onChange={(e) => setPeriod(e.target.value)}
      title="Period"
    >
      <option value="current">This month</option>
      <option value="last">Last month</option>
      <option value="all">All time</option>
    </select>
  ) : null;

  // Case spend is a pure dollar figure with no allotment equivalent — when
  // costs are hidden, drop the pill entirely rather than show a bare number.
  if (hideCosts && scope === 'case') return null;

  if (variant === 'compact') {
    if (scope === 'case') {
      const breakdown = data.breakdown || {};
      const chatTotal = breakdown.chat_client_usd || 0;
      const processingTotal = breakdown.processing_client_usd || 0;
      const showLlm = pricing?.show_llm_costs !== false;
      const showProcessing = pricing?.show_processing_costs !== false;
      const showProcessingRow = showProcessing && processingTotal > 0;
      const showChatRow = showLlm && chatTotal > 0;
      const showTooltip = total > 0 && (showProcessingRow || showChatRow);
      return (
        <span className={`meter-compact case${overflow ? ' overflow' : ''}`} title={`${fmt(total)} on this case (${periodLabel})`}>
          <span className="meter-label">Case spend</span>
          <span className="meter-amount">{fmt(total)}</span>
          {periodPicker}
          {showTooltip && (
            <span className="meter-breakdown-tooltip" role="tooltip">
              {showProcessingRow && (
                <span className="meter-breakdown-row">
                  <span className="meter-breakdown-label">Processing</span>
                  <span className="meter-breakdown-value">{fmt(processingTotal)}</span>
                </span>
              )}
              {showChatRow && (
                <span className="meter-breakdown-row">
                  <span className="meter-breakdown-label">Chat</span>
                  <span className="meter-breakdown-value">{fmt(chatTotal)}</span>
                </span>
              )}
            </span>
          )}
        </span>
      );
    }
    if (mode === 'allotment' && allowance) {
      const pct = Math.min(100, Math.round((total / allowance) * 100));
      const over = total > allowance;
      const title = hideCosts
        ? `${pct}% of your monthly allowance used (${periodLabel})`
        : `${fmt(total)} of your ${fmt(allowance)} monthly allowance used (${periodLabel})`;
      return (
        <span className={`meter-compact${over ? ' over' : ''}`} title={title}>
          <span className="meter-label">{usageLabel}</span>
          <span className="meter-bar">
            <span className="meter-bar-fill" style={{ width: `${Math.min(pct, 100)}%` }} />
          </span>
          <span className="meter-pct">{pct}%</span>
          {periodPicker}
        </span>
      );
    }
    // No allotment allowance to fall back on — a bare dollar amount is the
    // only thing to show, so hide the pill entirely when costs are off.
    if (hideCosts) return null;
    const atCeiling = ceiling != null && total >= ceiling;
    return (
      <span
        className={`meter-compact cost${overflow || atCeiling ? ' overflow' : ''}`}
        title={`${usageScope} across all cases: ${fmt(total)}${ceiling != null ? ` of your ${fmt(ceiling)} ceiling` : ''} (${periodLabel})`}
      >
        <span className="meter-label">{usageLabel}</span>
        <span className="meter-amount">{fmt(total)}</span>
        {periodPicker}
      </span>
    );
  }

  // detailed variant — when costs are hidden, suppress dollar figures and
  // show only the allotment bar (if any).
  if (hideCosts) {
    if (mode === 'allotment' && allowance) {
      const pct = Math.min((total / allowance) * 100, 100);
      return (
        <div className="meter-detailed">
          <div className="meter-allotment">
            <div className="meter-bar lg">
              <div className="meter-bar-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="meter-allowance-text">{Math.round(pct)}% of monthly usage</div>
          </div>
        </div>
      );
    }
    return <div className="meter-detailed"><div className="meter-row"><span className="meter-period">{periodLabel}</span></div></div>;
  }

  // detailed (settings page)
  return (
    <div className="meter-detailed">
      <div className="meter-row">
        <strong>{fmt(total)}</strong>
        <span className="meter-period"> · {periodLabel}</span>
        {periodPicker}
      </div>
      {mode === 'allotment' && allowance ? (
        <div className="meter-allotment">
          <div className="meter-bar lg">
            <div
              className="meter-bar-fill"
              style={{ width: `${Math.min((total / allowance) * 100, 100)}%` }}
            />
            {total > allowance ? (
              <div
                className="meter-bar-overflow"
                style={{ width: `${Math.min(((total - allowance) / allowance) * 100, 100)}%` }}
              />
            ) : null}
          </div>
          <div className="meter-allowance-text">
            {fmt(total)} of {fmt(allowance)} included
            {data.cap_mode === 'hard' && total >= allowance && (
              <span className="meter-cap-hit"> · cap reached</span>
            )}
          </div>
        </div>
      ) : null}
      {ceiling != null ? (
        <div className="meter-allowance-text">
          {fmt(total)} of your {fmt(ceiling)} spending ceiling
          {total >= ceiling && <span className="meter-cap-hit"> · ceiling reached — work is paused</span>}
        </div>
      ) : null}
      {data.plan_type && data.plan_type !== 'none' ? (
        <div className="meter-plan-info">
          Plan: <code>{data.plan_type}</code> · cap mode: <code>{data.cap_mode}</code>
        </div>
      ) : null}
    </div>
  );
}
